import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { fixedDecimal } from '@/lib/utils'

import { useCurrencyOptions } from '@/composables/data-grid'
import { usePriceService } from '@/composables/price.service'

const EFFORT_DECIMALS = 6

export const invoiceLineLabel = (row) => row.listing_item_text || row.description

export const paymentDaysLabel = (days) => `${days} ${getI18n().t('common.day', days)}`

export const isValidPaymentDays = (days) => Number.isInteger(days) && days > 0

export const dueDateFrom = (issueDate, paymentDays) => (issueDate && isValidPaymentDays(paymentDays)
  ? dayjs(issueDate).add(paymentDays, 'day').format('YYYY-MM-DD')
  : null)

export const useInvoiceLines = ({ toPriceData, fromPriceData = (changes) => changes, unitPriceFields, isComment, isEffortBased = null, isQuoteLine = false, invoiceId = null }) => {
  const i18n = getI18n()
  const {
    getTotalNoVatValue,
    getTotalVatValue,
    setDiscountValue,
    setChargePercentageValue,
    setTotalNoVatAdjustmentValue,
    setTotalVatAdjustmentValue,
  } = usePriceService()

  const isInvoicedHere = (row) => invoiceId != null && row.invoice === invoiceId
  const isInvoiceable = (row) => row.invoice == null || isInvoicedHere(row)
  const effortFields = ['planned']
  const isEditable = (row, field = null) => !isInvoicedHere(row) && !isComment(row) && (!effortFields.includes(field) || !!isEffortBased?.(row))

  const toPercent = (fraction) => (fraction == null ? null : fixedDecimal(Number(fraction) * 100, EFFORT_DECIMALS))
  const fromPercent = (percent) => (percent == null || percent === '' ? null : percent / 100)

  const priceDataOf = (row) => toPriceData({ ...row, quantity: Number(row.billed_quantity) || 0 })

  const withDerivedValues = (row) => ({
    discount_percent: toPercent(row.discount),
    charge_percent: toPercent(row.charge_percentage),
    ...(isEffortBased ? { planned: isEffortBased(row) ? fixedDecimal((Number(row.billed_quantity) || 0) * (Number(row.amount_per_unit) || 0), EFFORT_DECIMALS) : null } : {}),
    total_no_vat: getTotalNoVatValue(priceDataOf(row)),
    total_vat: getTotalVatValue(priceDataOf(row)),
  })

  const applyPriceSetter = (setter, newData, value, row) => {
    const changes = {}
    setter(changes, value, priceDataOf({ ...row, ...newData }))
    const rowChanges = fromPriceData(changes)
    Object.assign(newData, rowChanges, withDerivedValues({ ...row, ...newData, ...rowChanges }))
  }

  const toLine = (row) => {
    const line = { ...row, billed_quantity: row.quantity }
    const original = { ...line, ...withDerivedValues(line) }
    return { ...original, original }
  }

  const quoteQuantityOf = (row) => Math.min(Number(row.billed_quantity) || 0, Number(row.original?.quantity) || 0)

  const quoteTotalsOf = (row) => withDerivedValues({
    ...row,
    billed_quantity: quoteQuantityOf(row),
    discount: row.original?.discount,
    charge_percentage: row.original?.charge_percentage,
    amount_per_unit: row.original?.amount_per_unit,
  })

  const isOverQuote = (row) => isQuoteLine && !isComment(row) && Number(row.total_no_vat) > Number(quoteTotalsOf(row).total_no_vat)

  const editableFields = ['billed_quantity', ...(isEffortBased ? effortFields : []), 'charge_percent', 'discount_percent', 'total_no_vat', 'total_vat']
  const totalFields = ['total_no_vat', 'total_vat']

  const isModified = (row, field) => Number(row[field] ?? 0).toFixed(6) !== Number(row.original?.[field] ?? 0).toFixed(6)

  const prepareEditableCell = (e) => {
    if (!editableFields.includes(e.column.name)) return
    if (e.rowType === 'header') {
      e.cellElement.style.backgroundColor = 'color-mix(in srgb, var(--q-primary) 12%, transparent)'
      return
    }
    if (e.rowType === 'data' && totalFields.includes(e.column.name)) e.cellElement.classList.add('text-bold')
    if (e.rowType !== 'data' || !isEditable(e.data, e.column.name)) return
    if (isModified(e.data, e.column.name)) {
      e.cellElement.classList.add('bg-orange-2')
    }
    if (totalFields.includes(e.column.name)) {
      const quoteTotal = Number(quoteTotalsOf(e.data)[e.column.name])
      const total = Number(e.data[e.column.name])
      if (total < quoteTotal) e.cellElement.classList.add('text-negative')
      if (total > quoteTotal) e.cellElement.classList.add('text-positive')
    }
  }

  const toPayload = (row) => ({
    id: row.id,
    discount: row.discount ?? null,
    charge_percentage: row.charge_percentage ?? null,
    ...Object.fromEntries(unitPriceFields.map(field => [field, row[field]])),
    ...(isComment(row) ? {} : { quantity: row.billed_quantity }),
    ...(isEditable(row, 'planned') ? { amount_per_unit: row.amount_per_unit } : {}),
    ...(isQuoteLine && isOverQuote(row) ? { update_quote: row.update_quote === true } : {}),
  })

  const hasQuantityToInvoice = (row) => isComment(row) || Number(row.billed_quantity) > 0

  const isPartial = (row) => !isComment(row) && Number(row.billed_quantity) > 0 && (
    Number(row.billed_quantity) < Number(row.quantity) ||
    (!!isEffortBased?.(row) && Number(row.planned) < Number(row.original?.planned))
  )

  const numberColumn = (dataField, caption, setCellValue, options = {}) => ({
    dataField,
    name: dataField,
    caption,
    dataType: 'number',
    width: '6rem',
    format: '#0.##',
    setCellValue,
    ...options,
  })

  const lineSetters = {
    billed_quantity: (newData, value, row) => {
      newData.billed_quantity = Math.max(Number(value) || 0, 0)
      Object.assign(newData, withDerivedValues({ ...row, ...newData }))
    },
    planned: (newData, value, row) => {
      const quantity = Number(row.billed_quantity) || 0
      newData.amount_per_unit = quantity ? fixedDecimal((Number(value) || 0) / quantity, EFFORT_DECIMALS) : row.amount_per_unit
      Object.assign(newData, withDerivedValues({ ...row, ...newData }))
    },
    charge_percent: (newData, value, row) => applyPriceSetter(setChargePercentageValue, newData, fromPercent(value), row),
    discount_percent: (newData, value, row) => applyPriceSetter(setDiscountValue, newData, fromPercent(value), row),
    total_no_vat: (newData, value, row) => applyPriceSetter(setTotalNoVatAdjustmentValue, newData, Number(value) || 0, row),
    total_vat: (newData, value, row) => applyPriceSetter(setTotalVatAdjustmentValue, newData, Number(value) || 0, row),
  }

  const setLineValue = (newData, field, value, row) => lineSetters[field](newData, value, row)

  const percentColumn = (dataField, caption) => numberColumn(dataField, caption, lineSetters[dataField], {
    format: { type: 'fixedPoint', precision: 2 },
    customizeText: ({ value, valueText }) => (value == null ? '' : `${valueText}%`),
  })

  const buildColumns = (currencyCode) => [
    numberColumn('billed_quantity', i18n.t('common.quantityToInvoice'), lineSetters.billed_quantity, { width: '8rem', format: { type: 'fixedPoint', precision: 2 } }),
    ...(isEffortBased ? [numberColumn('planned', i18n.t('common.planned'), lineSetters.planned)] : []),
    percentColumn('charge_percent', i18n.t('common.chargePercentage')),
    percentColumn('discount_percent', i18n.t('common.discount')),
    numberColumn('total_no_vat', i18n.t('common.totalNoVat'), lineSetters.total_no_vat, { ...useCurrencyOptions({ currency: currencyCode }), width: '9rem' }),
    numberColumn('total_vat', i18n.t('common.totalVat'), lineSetters.total_vat, { ...useCurrencyOptions({ currency: currencyCode }), width: '9rem' }),
  ]

  return {
    buildColumns,
    setLineValue,
    isInvoiceable,
    isInvoicedHere,
    isComment,
    isEditable,
    isOverQuote,
    toLine,
    toPayload,
    hasQuantityToInvoice,
    isPartial,
    prepareEditableCell,
  }
}
